import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { apiClient, ApiError } from '@/lib/api'
import { useT } from '@/i18n/I18nProvider'
import { AuthShell } from './SignInPage'

const Schema = z.object({
  email: z.string().email(),
  password: z.string().min(12, 'At least 12 characters').max(128),
  name: z.string().min(1).max(120).optional(),
  tenantName: z.string().min(2).max(80).default('My workspace'),
  accept: z.literal(true, { errorMap: () => ({ message: 'You must accept the terms' }) }),
})
type FormValues = z.infer<typeof Schema>

interface SignupResponse {
  token: string
  user: { id: string; email: string; name: string | null }
  tenant: { id: string; name: string; plan: string }
}

export function SignUpPage() {
  const t = useT()
  const navigate = useNavigate()
  const [serverError, setServerError] = useState<string | null>(null)
  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: { tenantName: 'My workspace' },
  })

  const signup = useMutation({
    mutationFn: (values: FormValues) => {
      const { accept: _accept, ...payload } = values
      return apiClient.post<SignupResponse>('/auth/signup', payload)
    },
    onSuccess: ({ token }) => {
      localStorage.setItem('discover.jwt', token)
      navigate('/onboard')
    },
    onError: (err) => setServerError(err instanceof ApiError ? err.message : 'Sign-up failed'),
  })

  return (
    <AuthShell>
      <h1 className="text-2xl font-semibold mb-1">{t('auth.signup.title')}</h1>
      <p className="text-sm opacity-70 mb-6">{t('auth.signup.subtitle')}</p>

      <form onSubmit={handleSubmit((v) => signup.mutate(v))} className="flex flex-col gap-3">
        <Field label={t('auth.name.optional')} error={errors.name?.message}>
          <input {...register('name')} className="auth-input" autoComplete="name" />
        </Field>
        <Field label={t('auth.workspace')} error={errors.tenantName?.message}>
          <input {...register('tenantName')} className="auth-input" />
        </Field>
        <Field label={t('auth.email')} error={errors.email?.message}>
          <input {...register('email')} type="email" autoComplete="email" className="auth-input" />
        </Field>
        <Field label={t('auth.password.new')} error={errors.password?.message}>
          <input {...register('password')} type="password" autoComplete="new-password" className="auth-input" />
        </Field>
        <label className="flex items-start gap-2 text-xs opacity-80 mt-1">
          <input type="checkbox" {...register('accept')} className="mt-0.5" />
          <span>
            {t('auth.accept.prefix')}{' '}
            <a href="/terms" target="_blank" rel="noreferrer" className="underline">{t('auth.terms')}</a>{' '}
            {t('auth.accept.and')}{' '}
            <a href="/privacy" target="_blank" rel="noreferrer" className="underline">{t('auth.privacy')}</a>.
          </span>
        </label>
        {errors.accept && <p className="text-xs" style={{ color: 'var(--color-critical)' }}>{errors.accept.message}</p>}

        {serverError && <p className="text-sm" style={{ color: 'var(--color-critical)' }}>{serverError}</p>}

        <button
          type="submit"
          disabled={signup.isPending}
          className="mt-1 px-3 py-2 rounded font-medium text-sm"
          style={{ background: 'var(--color-accent)', color: 'white' }}
        >
          {signup.isPending ? t('common.loading') : t('auth.signup.cta')}
        </button>
      </form>

      <p className="mt-6 text-sm opacity-80 text-center">
        {t('auth.signup.have_account')}{' '}
        <Link to="/auth/signin" className="underline">{t('auth.signin.link')}</Link>
      </p>
    </AuthShell>
  )
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm opacity-80">{label}</span>
      <div className="mt-1">{children}</div>
      {error && <p className="text-xs mt-1" style={{ color: 'var(--color-critical)' }}>{error}</p>}
    </label>
  )
}
